import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import { authClient } from "@/lib/auth-client";
import {
  isSystemRole,
  SYSTEM_ROLE_HIERARCHY,
  type SystemRole,
} from "@/lib/system-roles";
import { trpc } from "@/utils/trpc";

type SessionResult = ReturnType<typeof authClient.useSession>;

type UserContextValue = {
  session: SessionResult["data"];
  isSessionLoading: SessionResult["isPending"];
  systemRole: SystemRole | null;
  isSystemRoleLoading: boolean;
  isAuthenticated: boolean;
  isWorker: boolean;
  isManager: boolean;
  isAdmin: boolean;
  hasPermission: (requiredRole: SystemRole) => boolean;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const sessionResult = authClient.useSession();
  const { data: session, isPending: isSessionLoading } = sessionResult;

  const systemRoleOptions = trpc.getSystemRole.queryOptions();
  const { data: rawSystemRole, isLoading: isSystemRoleLoading } = useQuery({
    ...systemRoleOptions,
    enabled:
      Boolean(session?.user?.id) &&
      !isSessionLoading &&
      (systemRoleOptions.enabled ?? true),
  });

  const systemRole = isSystemRole(rawSystemRole) ? rawSystemRole : null;

  const value = useMemo<UserContextValue>(() => {
    const isAuthenticated = Boolean(session?.user?.id);

    const hasPermission = (requiredRole: SystemRole) =>
      systemRole !== null &&
      SYSTEM_ROLE_HIERARCHY[systemRole] >= SYSTEM_ROLE_HIERARCHY[requiredRole];

    return {
      session,
      isSessionLoading,
      systemRole,
      isSystemRoleLoading,
      isAuthenticated,
      isWorker: systemRole === "worker",
      isManager: systemRole === "site_manager",
      isAdmin: systemRole === "admin",
      hasPermission,
    };
  }, [session, isSessionLoading, systemRole, isSystemRoleLoading]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
}

export function useOptionalUser() {
  return useContext(UserContext);
}
